/**
 * Live Events Service
 * Handles API calls and data formatting for Live Events & Registrations
 */
import api from '../axiosconfig';

/**
 * Fetch live events with optional filtering
 * @param {Object} options
 * @param {'next30days'|'upcoming'|'past'|'month'} [options.filter='next30days']
 * @param {number} [options.month] - Month (1-12)
 * @param {number} [options.year] - Year (e.g. 2025)
 * @param {string} [options.category] - Category filter
 * @returns {Promise<Array>} List of structured LiveEvent objects
 */
export const getLiveEvents = async ({ filter = 'next30days', month, year, category } = {}) => {
  try {
    const params = { filter };
    if (month) params.month = month;
    if (year) params.year = year;
    if (category) params.category = category;

    const response = await api.get('/api/live-events', { params });
    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching live events:', error);
    return [];
  }
};

/**
 * Fetch month calendar data (events, offset, days)
 * @param {number} month - Month (1-12)
 * @param {number} year - Year (e.g. 2025)
 * @returns {Promise<{ events: Array, offset: number, days: number }>}
 */
export const getCalendarMonth = async (month, year) => {
  try {
    const response = await api.get('/api/live-events/calendar', {
      params: { month, year },
    });
    return {
      events: response?.data?.data || [],
      offset: response?.data?.offset ?? 0,
      days: response?.data?.days ?? 0,
    };
  } catch (error) {
    console.error('Error fetching calendar month data:', error);
    return { events: [], offset: 0, days: 0 };
  }
};

/**
 * Fetch details for a specific live event
 * @param {number|string} eventId
 * @returns {Promise<Object|null>}
 */
export const getLiveEventById = async (eventId) => {
  try {
    const response = await api.get(`/api/live-events/${eventId}`);
    return response?.data?.data || null;
  } catch (error) {
    console.error(`Error fetching event ${eventId}:`, error);
    return null;
  }
};

/**
 * Register current user for a live event
 * @param {number|string} eventId
 * @param {string} [email]
 * @param {string} [userTz]
 * @returns {Promise<{ success: boolean, result: number, message: string }>}
 */
export const registerForLiveEvent = async (eventId, email, userTz) => {
  try {
    const timezone =
      userTz || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Toronto";
    const payload = {
      ...(email ? { email } : {}),
      user_tz: timezone,
    };
    const response = await api.post(`/api/live-events/${eventId}/register`, payload);
    return {
      success: true,
      result: response?.data?.result ?? 1,
      message: response?.data?.message ?? 'Successfully registered',
    };
  } catch (error) {
    const data = error.response?.data || {};
    return {
      success: false,
      result: data.result ?? -1,
      message: data.message || data.error || 'Registration failed',
    };
  }
};

/**
 * Unregister current user from a live event
 * @param {number|string} eventId
 * @returns {Promise<{ success: boolean, result: number, message: string }>}
 */
export const unregisterFromLiveEvent = async (eventId) => {
  try {
    const response = await api.delete(`/api/live-events/${eventId}/register`);
    return {
      success: true,
      result: response?.data?.result ?? 1,
      message: response?.data?.message ?? 'Successfully unregistered',
    };
  } catch (error) {
    const data = error.response?.data || {};
    return {
      success: false,
      result: data.result ?? -1,
      message: data.message || data.error || 'Unregister failed',
    };
  }
};

/**
 * Get current user's registered events
 * @returns {Promise<Array>}
 */
export const getMyRegistrations = async () => {
  try {
    const response = await api.get('/api/live-events/my-registrations');
    return response?.data?.data || [];
  } catch (error) {
    console.error('Error fetching my registrations:', error);
    return [];
  }
};

/**
 * Get secure join URL after validating registration
 * @param {number|string} eventId
 * @returns {Promise<{ success: boolean, stream_link?: string, title?: string, presenter?: string, error?: string, code?: string }>}
 */
export const getSecureJoinUrl = async (eventId) => {
  try {
    const response = await api.get(`/api/live-events/${eventId}/join-url`);
    return response?.data || { success: false, error: 'Unable to retrieve stream link' };
  } catch (error) {
    const data = error.response?.data || {};
    return {
      success: false,
      error: data.error || 'You are not authorized to join this session.',
      code: data.code || 'UNAUTHORIZED',
    };
  }
};

const liveEventsService = {
  getLiveEvents,
  getCalendarMonth,
  getLiveEventById,
  registerForLiveEvent,
  unregisterFromLiveEvent,
  getMyRegistrations,
  getSecureJoinUrl,
};

export default liveEventsService;
